/**
 * Un seul onglet ÉCRAN actif par établissement dans un même navigateur — sauf choix
 * explicite de garder les deux.
 *
 * Pourquoi : tout onglet écran d'un bar (menu `/home` ou jeu `/app/...`) suit la
 * clé partagée `screenInfos.anim` — le menu entre dans le jeu choisi ailleurs
 * (HomeAnimation), le jeu suit les changements de jeu (useRemoteGameSwitch). Un
 * animateur qui rouvre la console dans un nouvel onglet (lien du site, du
 * dashboard, favori) garde les anciens ouverts en arrière-plan : chaque jeu lancé
 * part alors dans TOUS les onglets, chacun avec son propre code de salle. Résultat
 * vu le 10/10/2026 (essai UK Ent hero, résilié 2 h après pour « lots of bugs ») :
 * jusqu'à 4 Blind Test / UnQuiz qui jouent en même temps, et les téléphones à
 * rescanner à chaque nouvel onglet. Sur 30 jours, 26 bars sur 118 ont eu au moins
 * une partie lancée en double, deux fois sur trois avec un onglet caché.
 *
 * Protocole (BroadcastChannel : ne sort jamais du navigateur, TV + PC distincts
 * ne sont pas concernés). À chaque démarrage d'écran (chargement, menu ↔ jeu),
 * l'onglet dit `hello` ; les autres écrans actifs du même bar répondent `here`.
 * - Le nouvel onglet est VISIBLE et l'autre écran est CACHÉ (onglet oublié derrière,
 *   le cas réel) → l'autre se met en veille, sans question (`take` ciblé).
 * - Le nouvel onglet est VISIBLE et un autre écran est VISIBLE aussi → on DEMANDE
 *   (calque) : « N'utiliser que cet onglet » (`take` : les autres se mettent en
 *   veille) ou « Garder les deux » (`both` : deux télés branchées sur un même PC,
 *   mémorisé par les onglets visibles ; un onglet caché à ce moment se met en veille).
 * - Il est CACHÉ (ouvert en arrière-plan, ou menu caché entraîné dans un jeu par
 *   le cockpit) et un autre écran du bar est visible → il se met lui-même en
 *   veille, sans rien demander (si tous sont cachés : rien ne change).
 * Jamais de mise en veille silencieuse d'un onglet VISIBLE. Un onglet caché, lui, se
 * met en veille sans question, même en pleine partie : il ne passe sur aucune télé
 * (ses téléphones perdent la salle, comme à la fermeture de l'onglet).
 * Veille = rechargement complet vers une page d'attente : plus de socket, de jeu ni
 * de son ; « Utiliser cet onglet » y ramène.
 */

export const SCREEN_TAB_CHANNEL = 'ub-screen-tab';

/** Page d'attente d'un onglet mis en veille (route MainRoutes + AuthRoutes). */
export const DORMANT_PATH = '/other-tab';

type MessageType = 'hello' | 'here' | 'take' | 'both' | 'bye';
type TabMessage = {
    type: MessageType;
    clientID: string;
    tabId: string;
    /** `here` : l'émetteur est-il caché ? */
    hidden?: boolean;
    /** `take` : seul cet onglet se met en veille (absent = tous les autres). */
    target?: string;
    /** `here` : réponse destinée au seul onglet qui a dit `hello`. */
    to?: string;
};

type Channel = {
    postMessage: (message: TabMessage) => void;
    onmessage: ((event: { data: unknown }) => void) | null;
    close: () => void;
};

export type ScreenTabDeps = {
    /** null si BroadcastChannel est absent (vieux navigateur de TV) : rien ne change. */
    createChannel: () => Channel | null;
    isHidden: () => boolean;
    /** « Garder les deux » déjà choisi pour ce bar dans cet onglet. */
    keepsBoth: (clientID: string) => boolean;
    rememberBoth: (clientID: string) => void;
    goDormant: () => void;
};

export type ScreenTabLock = {
    /** « Utiliser cet onglet » : les autres écrans du bar se mettent en veille. */
    takeOver: () => void;
    /** « Garder les deux » : plus de question pour ce bar, dans aucun onglet. */
    keepBoth: () => void;
    stop: () => void;
};

/**
 * `onOthers(n)` : nombre d'autres écrans actifs du bar sur lesquels la personne
 * doit trancher (0 = rien à demander, ou plus rien à demander).
 */
export function startScreenTabLock(
    clientID: string,
    deps: ScreenTabDeps,
    tabId: string,
    onOthers: (count: number) => void = () => undefined
): ScreenTabLock {
    const noop: ScreenTabLock = { takeOver: () => undefined, keepBoth: () => undefined, stop: () => undefined };
    let channel: Channel | null = null;
    try {
        channel = deps.createChannel();
    } catch {
        channel = null;
    }
    if (!channel) return noop;
    const ch = channel;

    let dormant = false;
    let stopped = false;
    const pending = new Set<string>();
    const post = (type: MessageType, extra: Partial<TabMessage> = {}) => {
        try {
            const hidden = type === 'here' ? { hidden: deps.isHidden() } : {};
            ch.postMessage({ type, clientID, tabId, ...hidden, ...extra });
        } catch {
            /* canal fermé : rien à signaler */
        }
    };
    const publish = () => onOthers(pending.size);
    const sleep = () => {
        if (dormant) return;
        dormant = true;
        pending.clear();
        publish();
        post('bye');
        deps.goDormant();
    };

    ch.onmessage = (event) => {
        const msg = event.data as Partial<TabMessage> | null;
        if (stopped || dormant || !msg || typeof msg.tabId !== 'string') return;
        if (msg.clientID !== clientID || msg.tabId === tabId) return;
        switch (msg.type) {
            case 'hello':
                post('here', { to: msg.tabId });
                break;
            case 'here':
                // Seul le nouvel onglet tranche : les autres ignorent les réponses qui ne leur sont pas destinées.
                if (msg.to !== tabId) break;
                if (deps.keepsBoth(clientID)) break;
                if (deps.isHidden()) {
                    if (msg.hidden === false) sleep();
                } else if (msg.hidden) {
                    // Écran oublié dans un onglet caché : il ne passe sur aucune télé.
                    post('take', { target: msg.tabId });
                } else if (!pending.has(msg.tabId)) {
                    pending.add(msg.tabId);
                    publish();
                }
                break;
            case 'take':
                if (msg.target === undefined || msg.target === tabId) sleep();
                break;
            case 'both':
                // « Les deux » = les télés affichées ; un onglet caché à ce moment est un oubli.
                if (deps.isHidden()) {
                    sleep();
                    break;
                }
                deps.rememberBoth(clientID);
                if (pending.size) {
                    pending.clear();
                    publish();
                }
                break;
            case 'bye':
                if (pending.delete(msg.tabId)) publish();
                break;
            default:
                break;
        }
    };

    post('hello');

    return {
        takeOver: () => {
            if (stopped || dormant) return;
            post('take');
            pending.clear();
            publish();
        },
        keepBoth: () => {
            if (stopped || dormant) return;
            deps.rememberBoth(clientID);
            post('both');
            pending.clear();
            publish();
        },
        stop: () => {
            if (stopped) return;
            if (!dormant) post('bye');
            stopped = true;
            ch.onmessage = null;
            try {
                ch.close();
            } catch {
                /* déjà fermé */
            }
        },
    };
}

/**
 * Chemin de retour accepté par la page d'attente : l'écran du bar uniquement,
 * résolu sur l'origine courante (pas de redirection ouverte, pas de `/app/../`).
 */
export function safeScreenPath(back: string | null | undefined, origin: string): string | null {
    if (!back || !back.startsWith('/')) return null;
    let url: URL;
    try {
        url = new URL(back, origin);
    } catch {
        return null;
    }
    if (url.origin !== origin) return null;
    const { pathname } = url;
    if (!(pathname.startsWith('/app/') || pathname === '/home')) return null;
    return pathname + url.search;
}

/** `c` = établissement, lu une fois par la page d'attente pour la mesure GA4 puis retiré. */
export function dormantURL(currentPath: string, clientID?: string) {
    const c = clientID ? `&c=${encodeURIComponent(clientID)}` : '';
    return `${DORMANT_PATH}?back=${encodeURIComponent(currentPath)}${c}`;
}

/** Vrai pendant la mise en veille : le « Quitter le site ? » des jeux ne doit pas la bloquer. */
let leavingForOtherTab = false;
export function isLeavingForOtherTab() {
    return leavingForOtherTab;
}
export function markLeavingForOtherTab() {
    leavingForOtherTab = true;
}
